import React, { FC, useState, useEffect, useRef, useContext } from "react";
import useProjectStore, { DistanceUnit } from "common/store/ProjectStore";
import DeviceRange from "./DeviceRange";
import { Menu, MenuItem, Typography } from "@mui/material";
import theme from "common/theme/theme";
import useDeviceStore from "common/store/DeviceStore";
import DeviceCategory from "common/constants/DeviceCategory";
import useNotificationStore from "common/store/NotificationStore";
import useLookupsStore from "common/store/LookupsStore";
import { FeetMetersConversion } from "common/constants/GlobalValues";
import { ObstructionType } from "./DroppedObstruction";
import { LanguageContext } from "common/contexts/LanguageContext";
import { SharedProjectContext } from "components/project/Project";
import DragSelected, { IDeviceShadow } from "./DragSelected";

interface IProps {
  name: string;
  imageUrl: string;
  initialX: number;
  initialY: number;
  networkID: number;
  instanceID: number;
  instanceIndex: number;
  compliant: boolean;
  setCompliant: (b: boolean) => void;
  selected: boolean;
  setSelected: (b: boolean) => void;
  unselectDevices: () => void;
  setSelectMetadata: (metadata: { currentID: number; multiselect: boolean }) => void;
  initial: boolean;
  deleteDevice: (n: number) => void;
  coordsUpdated: { updated: boolean; index: number };
  setCoordsUpdated: (coordsUpdated: { updated: boolean; index: number }) => void;
  setUpdateComplete: () => void;
  handlePropertiesRequest: () => void;
  handleCopy: () => void;
  setNeighbors: (n: number[]) => void;
  path: boolean;
  obstructionUpdated: { updated: boolean; index: number };
  setObstructionUpdated: (obstructionUpdated: { updated: boolean; index: number }) => void;
  setObUpdateComplete: () => void;
  plotImageTransform: number;
  deviceMeta: Map<number, { selected: boolean; initial: boolean; neighbors: number[]; path: boolean }>;
  multiDrag: number[];
  setMultiDrag: (n: number[]) => void;
}

const DroppedDevice: FC<IProps> = (props) => {
  const {
    name,
    imageUrl,
    initialX,
    initialY,
    networkID,
    instanceID,
    instanceIndex,
    compliant,
    setCompliant,
    selected,
    setSelected,
    unselectDevices,
    setSelectMetadata,
    initial,
    deleteDevice,
    coordsUpdated,
    setCoordsUpdated,
    setUpdateComplete,
    handlePropertiesRequest,
    handleCopy,
    setNeighbors,
    path,
    obstructionUpdated,
    setObstructionUpdated,
    setObUpdateComplete,
    plotImageTransform,
    deviceMeta,
    multiDrag,
    setMultiDrag,
  } = props;

  const {
    zoomPercent,
    iconPercent,
    scalingFactor,
    activeNetwork,
    activeGateway,
    activeNetworkContainerID,
    updateDeviceInstances,
    updateGateway,
    enableStressTest,
    distanceID,
    obstructionInstances,
  } = useProjectStore();
  const { lookupCategory } = useDeviceStore();
  const lookupDeviceImage = useDeviceStore((state) => state.lookupDeviceImage);
  const { updateRates } = useLookupsStore();
  const { enqueueNotification, currentBatchID } = useNotificationStore();
  const { getString } = useContext(LanguageContext);
  const { calculationsToggle, bladesViewAvailable } = useContext(SharedProjectContext);
  const firstUpdateRange = useRef(true);

  // location (x,y) on the draggable icon that the cursor is located
  const [cursorX, setCursorX] = useState<number>(0);
  const [cursorY, setCursorY] = useState<number>(0);

  // coordinate center of the dropped device image relative to 'drop-box' when zoom is at 100% (constant coordinates)
  const [centerX, setCenterX] = useState<number>();
  const [centerY, setCenterY] = useState<number>();

  // coordinate center of the dropped device image relative to 'drop-box' adjusted for zoom (used coordinates)
  const [calculatedX, setCalculatedX] = useState<number>();
  const [calculatedY, setCalculatedY] = useState<number>();

  // icon height and width in pixels. (square)
  const iconHW = 40;
  const [deviceType, setDeviceType] = useState<number>(-1);
  const [deviceIcon, setDeviceIcon] = useState<string>();

  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [openDeviceMenu, setOpenDeviceMenu] = useState(false);
  const [range, setRange] = useState<number>(1);
  const [showDeviceRange, setShowDeviceRange] = useState(false);
  const [antennaOutOfRange, setAntennaOutOfRange] = useState(false);
  // contains devices in range, not blocked by obstructions
  const [devicesInRange, setDevicesInRange] = useState<number[]>([]);
  const [measurementUnit, setMeasurementUnit] = useState<number>();
  // contains an array of all devices in range, whether or not they are blocked by an obstruction
  const [allNeighbors, setAllNeighbors] = useState<number[]>([]);
  //multi-drag numbers to track
  const [cursorOnDragX, setCursorOnDragX] = useState<number | null>();
  const [cursorOnDragY, setCursorOnDragY] = useState<number | null>();
  const [multiDragShadow, setMultiDragShadow] = useState<IDeviceShadow[]>([]);

  const transformViewportCoords = () => {
    setCenterX(initialX * plotImageTransform);
    setCenterY(initialY * plotImageTransform);
    setCalculatedX(initialX * plotImageTransform - iconHW / 2);
    setCalculatedY(initialY * plotImageTransform - iconHW / 2);
  };

  useEffect(() => {
    transformViewportCoords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plotImageTransform]);

  useEffect(() => {
    var deviceID = instanceIndex === -1 ? activeGateway!.deviceID : activeNetwork!.deviceInstances[instanceIndex].deviceID;
    setDeviceType(lookupCategory(deviceID));
    if (initial && initialX !== -1 && initialY !== -1) {
      // if the device is being dropped in for the first time
      // set initial to false
      const dev = instanceIndex !== -1 ? activeNetwork!.deviceInstances[instanceIndex] : activeGateway!;
      dev.initial = false;
      if (instanceIndex !== -1) {
        updateDeviceInstances(networkID, dev);
      } else {
        updateGateway(activeNetworkContainerID, dev);
      }

      var boxElement = document.getElementById("drop-box");
      if (boxElement) {
        var box = boxElement.getBoundingClientRect();
        //adjust centerX,centerY if zoomed in (else will be dividing by 1 if zoom is 100)
        const newCenterX = (initialX - box.left + boxElement.scrollLeft) / (zoomPercent / 100);
        const newCenterY = (initialY - box.top + boxElement.scrollTop) / (zoomPercent / 100);
        setCenterX(newCenterX);
        setCenterY(newCenterY);
        setCalculatedX(newCenterX - iconHW / 2);
        setCalculatedY(newCenterY - iconHW / 2);

        updateCoords(newCenterX / plotImageTransform, newCenterY / plotImageTransform);
      }
    } else {
      transformViewportCoords();
      setCoordsUpdated({ updated: true, index: instanceIndex });
      setDeviceIcon(lookupDeviceImage(deviceID));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialX]); //dependency array should only contain initialX //, [initialX]

  function handleOnDrag(e: React.DragEvent) {
    if (centerX && centerY) {
      setCursorX(e.clientX);
      setCursorY(e.clientY);

      if (selected) {
        var devices: number[] = [];
        var deviceInfo: IDeviceShadow[] = [];
        deviceMeta.forEach((value, key) => {
          if (value.selected) {
            devices = [...devices, key];
            var index = activeNetwork!.deviceInstances.findIndex((dev) => dev.deviceInstanceID === key);
            if (index !== -1) {
              var dev = { ...activeNetwork!.deviceInstances[index] }
            } else {
              dev = { ...activeGateway! };
            }
            if (dev) {
              deviceInfo = [
                ...deviceInfo,
                {
                  offsetX: centerX - dev.xPosition * plotImageTransform,
                  offsetY: centerY - dev.yPosition * plotImageTransform,
                  image: lookupDeviceImage(dev.deviceID),
                },
              ];
            }
          }
        });
        
        setMultiDrag(devices);
        setMultiDragShadow(deviceInfo);
      }
    }
  }

  function handleOnDragging(e: React.DragEvent) {
    setCursorOnDragX(e.clientX);
    setCursorOnDragY(e.clientY);
  }

  function handleOnDrop(e: React.DragEvent) {
    var boxElement = document.getElementById("plot-plan-view-port");
    if (boxElement) {
      var box = boxElement.getBoundingClientRect();
      var boxLeft = box.left;
      //if devicesBox is open, get the box width and add it to box left
      if (bladesViewAvailable) {
        var bladeElement = document.getElementById("device-blade-overlay");
        if (bladeElement) {
          var bladeBox = bladeElement.getBoundingClientRect();
          boxLeft = boxLeft + bladeBox.width;
        }
      }
      if (multiDrag.length > 1) {
        var topmostY = -1;
        var bottommostY = -1;
        var leftmostX = -1;
        var rightmostX = -1;
        multiDrag.forEach((value) => {
          if (value !== activeGateway?.deviceInstanceID) {
            var dev = { ...activeNetwork!.deviceInstances.find((dev) => dev.deviceInstanceID === value)};
          } else {
            dev = { ...activeGateway! };
          }
          if (dev && dev.xPosition && dev.yPosition) {
            if (topmostY === -1) {
              topmostY = dev.yPosition;
              bottommostY = dev.yPosition;
              leftmostX = dev.xPosition;
              rightmostX = dev.xPosition;
            } else {
              topmostY = topmostY < dev.yPosition ? topmostY : dev.yPosition;
              bottommostY = bottommostY > dev.yPosition ? bottommostY : dev.yPosition;
              leftmostX = leftmostX < dev.xPosition ? leftmostX : dev.xPosition;
              rightmostX = rightmostX > dev.xPosition ? rightmostX : dev.xPosition;
            }
          }
        });
        const newCenterX = calculatedX! + (e.clientX - cursorX) / (zoomPercent / 100) + iconHW / 2;
        const newCenterY = calculatedY! + (e.clientY - cursorY) / (zoomPercent / 100) + iconHW / 2;
        var xDiff = centerX! - newCenterX;
        var yDiff = centerY! - newCenterY;

        rightmostX = rightmostX * plotImageTransform - centerX!;
        leftmostX = centerX! - leftmostX * plotImageTransform;
        bottommostY = bottommostY * plotImageTransform - centerY!;
        topmostY = centerY! - topmostY * plotImageTransform;

        if (
          box.right > e.clientX + rightmostX &&
          e.clientX - leftmostX > boxLeft &&
          box.bottom > e.clientY + bottommostY &&
          e.clientY - topmostY > box.top
        ) {
          updateMultipleCoords(xDiff / plotImageTransform, yDiff / plotImageTransform);
        }
      } else {
        if (box.right > e.clientX && e.clientX > boxLeft && box.bottom > e.clientY && e.clientY > box.top) {
          const newCenterX = calculatedX! + (e.clientX - cursorX) / (zoomPercent / 100) + iconHW / 2;
          const newCenterY = calculatedY! + (e.clientY - cursorY) / (zoomPercent / 100) + iconHW / 2;
          //adjust centerX,CenterY if zoomed in/out (else zoomPercent/100 will equal 1)
          setCenterX(newCenterX);
          setCenterY(newCenterY);
          setCalculatedX(newCenterX - iconHW / 2);
          setCalculatedY(newCenterY - iconHW / 2);

          //update device x and y positions in project store (center xy)
          updateCoords(newCenterX / plotImageTransform, newCenterY / plotImageTransform);
        } else {
          e.stopPropagation();
        }
      }
      setCursorOnDragX(null);
      setCursorOnDragY(null);
      setMultiDrag([]);
      setMultiDragShadow([]);
    }
  }

  useEffect(() => {
    if (firstUpdateRange.current) {
      setMeasurementUnit(distanceID);
      firstUpdateRange.current = false;
    } else {
      if (deviceType !== DeviceCategory.Gateway) {
        var dev = { ...activeNetwork!.deviceInstances[instanceIndex] };
      } else {
        dev = { ...activeGateway! };
      }
      if (distanceID !== measurementUnit) {
        if (distanceID === DistanceUnit.Feet) {
          //convert from meters to feet
          dev.range = dev.range * FeetMetersConversion;
          dev.height = dev.height * FeetMetersConversion;
        } else {
          //convert from feet to meters
          dev.range = dev.range / FeetMetersConversion;
          dev.height = dev.height / FeetMetersConversion;
        }
        if (deviceType !== DeviceCategory.Gateway) {
          updateDeviceInstances(networkID, dev);
        } else {
          updateGateway(activeNetworkContainerID, dev);
        }
        setMeasurementUnit(distanceID);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distanceID]);

  // handle clicks: right-click to open the device menu and left-click to select
  function openMenu(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    setAnchor(e.currentTarget);
    setOpenDeviceMenu(true);
  }

  function selectDevice(e: React.MouseEvent) {
    if (!selected) {
      unselectDevices();
      setSelectMetadata({ currentID: instanceID, multiselect: false });
    }
    setSelected(!selected);
  }

  function updateCoords(x: number, y: number) {
    if (instanceIndex === -1) {
      var dev = { ...activeGateway! };
      dev.xPosition = x;
      dev.yPosition = y;
      updateGateway(activeNetworkContainerID, dev);
    } else {
      dev = { ...activeNetwork!.deviceInstances[instanceIndex] };
      dev.xPosition = x;
      dev.yPosition = y;
      updateDeviceInstances(networkID, dev);
    }
  }

  function updateMultipleCoords(x: number, y: number) {
    multiDrag.forEach((value) => {
      //try and get index
      var index = activeNetwork!.deviceInstances.findIndex((dev) => dev.deviceInstanceID === value);
      if (index !== -1) {
        var dev = { ...activeNetwork!.deviceInstances[index] }
      } else {
        dev = { ...activeGateway! };
      }
      if (dev) {
        dev.xPosition = dev.xPosition - x;
        dev.yPosition = dev.yPosition - y;
        if (index !== -1) {
          updateDeviceInstances(networkID, dev);
        } else {
          updateGateway(activeNetworkContainerID, dev);
        }
      }
    });
  }

  useEffect(() => {
    if (!calculationsToggle) {
      setCompliant(true);
      if (deviceType === DeviceCategory.Antenna) {
        setAntennaOutOfRange(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculationsToggle]);

  // Obstruction checks
  useEffect(() => {
    if (obstructionUpdated.updated) {
      if (calculationsToggle) {
        updatedObstructionCheck();
        setObUpdateComplete();
      } else {
        setObstructionUpdated({ updated: false, index: -1 });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obstructionUpdated]);

  // Range checks
  useEffect(() => {
    if (coordsUpdated.updated) {
      if (calculationsToggle) {
        rangeCheck();
        setUpdateComplete();
      } else {
        if (!scalingFactor) {
          throw new Error(getString("nwk_rules_scaling_factor"));
        }
        setCoordsUpdated({ updated: false, index: -1 });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordsUpdated]);

  useEffect(() => {
    if(deviceType !== -1) {
      if (deviceType !== DeviceCategory.Gateway) {
        var updateRateID = activeNetwork!.deviceInstances[instanceIndex].updateRateID;
        var range = activeNetwork!.deviceInstances[instanceIndex].range;
      } else {
        updateRateID = activeGateway!.updateRateID;
        range = activeGateway!.range;
      }
      const updateRate = updateRates.find((rate) => rate.updateRateID === updateRateID)?.updateRateSeconds;
  
      var half = 1;
      if (updateRate! < 2) {
        half = 2;
      }
      const rangeValue = (range * scalingFactor! * plotImageTransform * (enableStressTest / 100)) / half;
      if (isFinite(rangeValue)) {
        setRange(rangeValue);
      }
    }
  }, [activeNetwork, scalingFactor, plotImageTransform, enableStressTest, instanceIndex, updateRates, deviceType, activeGateway]);

  const handleDoubleClick = () => {
    handlePropertiesRequest();
  };

  function rangeCheck() {
    try {
      const val = checkRules();
      setCompliant(val);
    } catch (e) {
      if (e instanceof Error) {
        notify(e.message);
      } else {
        console.error("An unexpected error occured:", e);
      }
    }
  }
  function checkRules() {
    if (!scalingFactor) {
      throw new Error(getString("nwk_rules_scaling_factor"));
    }
    var selfX = initialX*plotImageTransform
    var selfY = initialY*plotImageTransform
    if (deviceType === DeviceCategory.Antenna) {
      // Check if antenna and gateway are within range of each other
      var dist = distanceFormula(
        selfX! / plotImageTransform,
        selfY! / plotImageTransform,
        activeGateway!.xPosition,
        activeGateway!.yPosition,
      );
      dist = dist / scalingFactor; // will translate dist from pixels to measurementUnit
      var ret = dist <= activeGateway!.range * (enableStressTest / 100);
      if (!ret) {
        notify(getString("nwk_rules_antenna_gateway"));
      }
      setAntennaOutOfRange(!ret);

      //rule of five minimum within range of gateway (antenna)
      var inRange = 0;
      var arrayToSave: number[] = [];
      if (devicesInRange.length === 0 || coordsUpdated.index === -1 || coordsUpdated.index === instanceIndex) {
        setDevicesInRange([]);
        setAllNeighbors([]);
        activeNetwork!.deviceInstances.forEach((device, i) => {
          const deviceCat = lookupCategory(device.deviceID);
          if (deviceCat !== DeviceCategory.Gateway && deviceCat !== DeviceCategory.Antenna) {
            //exclude gateway and antenna from the count
            var dist = distanceFormula(
              selfX! / plotImageTransform,
              selfY! / plotImageTransform,
              device.xPosition,
              device.yPosition,
            );
            dist = dist / scalingFactor; // will translate dist from pixels to measurementUnit
            if (dist <= activeNetwork!.deviceInstances[0].range * (enableStressTest / 100)) {
              setAllNeighbors((prevArray) => [...prevArray, i]);
              var ob = obstructionsCheck(
                selfX! / plotImageTransform,
                selfY! / plotImageTransform,
                device.xPosition,
                device.yPosition,
              );
              if (ob) {
                //in range and not blocked by an obstruction, add to count
                arrayToSave = [...arrayToSave, i];
                setDevicesInRange((prevArray) => [...prevArray, i]);
                inRange++;
              }
            }
          }
        });
      } else {
        inRange = devicesInRange.length;
        arrayToSave = [...devicesInRange];
        const deviceCat = lookupCategory(activeNetwork!.deviceInstances[coordsUpdated.index].deviceID);
        if (deviceCat !== DeviceCategory.Gateway) {
          dist = distanceFormula(
            selfX! / plotImageTransform,
            selfY! / plotImageTransform,
            activeNetwork!.deviceInstances[coordsUpdated.index].xPosition,
            activeNetwork!.deviceInstances[coordsUpdated.index].yPosition,
          );
          dist = dist / scalingFactor;
          var isInRange = dist <= activeNetwork!.deviceInstances[instanceIndex].range * (enableStressTest / 100);
          var inArray = devicesInRange.includes(coordsUpdated.index);
          var inAllNeighbors = allNeighbors.includes(coordsUpdated.index);
          if (inArray && !isInRange) {
            inRange--;
            arrayToSave = arrayToSave.filter((d) => d !== coordsUpdated.index);
            setDevicesInRange((prevArray) => prevArray.filter((d) => d !== coordsUpdated.index));
            setAllNeighbors((prevArray) => prevArray.filter((d) => d !== coordsUpdated.index));
          } else if (!inArray && isInRange) {
            setAllNeighbors((prevArray) => [...prevArray, coordsUpdated.index]);
            var ob = obstructionsCheck(
              selfX! / plotImageTransform,
              selfY! / plotImageTransform,
              activeNetwork!.deviceInstances[coordsUpdated.index].xPosition,
              activeNetwork!.deviceInstances[coordsUpdated.index].yPosition,
            );
            if (ob) {
              inRange++;
              arrayToSave = [...arrayToSave, coordsUpdated.index];
              setDevicesInRange((prevArray) => [...prevArray, coordsUpdated.index]);
            }
          } else if (inAllNeighbors && !inArray && !isInRange) {
            //then remove from all neighbors
            setAllNeighbors((prevArray) => prevArray.filter((d) => d !== coordsUpdated.index));
          }
        }
      }
      setNeighbors(arrayToSave);
      return antennaRules(inRange);
    } else if (deviceType !== DeviceCategory.Gateway && deviceType !== -1) {
      const updateRate = updateRates.find(
        (rate) => rate.updateRateID === activeNetwork!.deviceInstances[instanceIndex].updateRateID,
      )?.updateRateSeconds;
      var updateRateLessThanTwo = "";
      //rule of three -- minimum of three neighbors within range
      if (devicesInRange.length === 0 || coordsUpdated.index === -1 || coordsUpdated.index === instanceIndex) {
        setDevicesInRange([]);
        setAllNeighbors([]);
        arrayToSave = [];
        inRange = 0;
        //iterate through all devices and check if any are in range (add to array if yes)
        activeNetwork!.deviceInstances.forEach((device, i) => {
          const deviceCat = lookupCategory(device.deviceID);
          if (deviceCat !== DeviceCategory.Gateway && deviceCat !== DeviceCategory.Antenna && i !== instanceIndex) {
            //exclude gateway and antenna from the count, exclude self
            var dist = distanceFormula(
              selfX! / plotImageTransform,
              selfY! / plotImageTransform,
              device.xPosition,
              device.yPosition,
            );
            dist = dist / scalingFactor; // will translate dist from pixels to measurementUnit
            //rule of maximum distance -- devices faster than 2 secs need to be within 2x distance
            if (updateRate! < 2) {
              updateRateLessThanTwo = getString("nwk_rules_update_rate");
              dist = dist * 2;
            }
            if (dist <= activeNetwork!.deviceInstances[instanceIndex].range * (enableStressTest / 100)) {
              //dist is in range
              setAllNeighbors((prevArray) => [...prevArray, i]);
              ob = obstructionsCheck(
                selfX! / plotImageTransform,
                selfY! / plotImageTransform,
                device.xPosition,
                device.yPosition,
              );
              if (ob) {
                arrayToSave = [...arrayToSave, i];
                setDevicesInRange((prevArray) => [...prevArray, i]);
                inRange++;
              }
            }
          }
        });
      } else {
        inRange = devicesInRange.length;
        arrayToSave = [...devicesInRange];
        const deviceCat = lookupCategory(activeNetwork!.deviceInstances[coordsUpdated.index].deviceID);
        if (deviceCat !== DeviceCategory.Antenna && deviceCat !== DeviceCategory.Gateway) {
          //check if deviceInstance[coordsUpdated.index] is in range
          dist = distanceFormula(
            selfX! / plotImageTransform,
            selfY! / plotImageTransform,
            activeNetwork!.deviceInstances[coordsUpdated.index].xPosition,
            activeNetwork!.deviceInstances[coordsUpdated.index].yPosition,
          );
          dist = dist / scalingFactor;
          //rule of maximum distance -- devices faster than 2 secs need to be within 2x distance
          if (updateRate! < 2) {
            updateRateLessThanTwo = getString("nwk_rules_update_rate");
            dist = dist * 2;
          }
          isInRange = dist <= activeNetwork!.deviceInstances[instanceIndex].range * (enableStressTest / 100);
          inArray = devicesInRange.includes(coordsUpdated.index);
          inAllNeighbors = allNeighbors.includes(coordsUpdated.index);
          if (inArray && !isInRange) {
            inRange--;
            arrayToSave = arrayToSave.filter((d) => d !== coordsUpdated.index);
            setDevicesInRange((prevArray) => prevArray.filter((d) => d !== coordsUpdated.index));
            setAllNeighbors((prevArray) => prevArray.filter((d) => d !== coordsUpdated.index));
          } else if (!inArray && isInRange) {
            setAllNeighbors((prevArray) => [...prevArray, coordsUpdated.index]);
            ob = obstructionsCheck(
              selfX! / plotImageTransform,
              selfY! / plotImageTransform,
              activeNetwork!.deviceInstances[coordsUpdated.index].xPosition,
              activeNetwork!.deviceInstances[coordsUpdated.index].yPosition,
            );
            if (ob) {
              inRange++;
              arrayToSave = [...arrayToSave, coordsUpdated.index];
              setDevicesInRange((prevArray) => [...prevArray, coordsUpdated.index]);
            }
          } else if (inAllNeighbors && !inArray && !isInRange) {
            setAllNeighbors((prevArray) => prevArray.filter((d) => d !== coordsUpdated.index));
          }
          // else no changes needed to array
        }
      }
      //check if antenna needs to be added to arrayToSave
      dist = distanceFormula(
        selfX! / plotImageTransform,
        selfY! / plotImageTransform,
        activeNetwork!.deviceInstances[0].xPosition,
        activeNetwork!.deviceInstances[0].yPosition,
      );
      dist = dist / scalingFactor;
      //rule of maximum distance -- devices faster than 2 secs need to be within 2x distance
      if (updateRate! < 2) {
        dist = dist * 2;
      }
      if (dist <= activeNetwork!.deviceInstances[instanceIndex].range * (enableStressTest / 100)) {
        ob = obstructionsCheck(
          selfX! / plotImageTransform,
          selfY! / plotImageTransform,
          activeNetwork!.deviceInstances[0].xPosition,
          activeNetwork!.deviceInstances[0].yPosition,
        );
        if (ob) {
          arrayToSave = [...arrayToSave, 0]; // the 0 refers to the index of the antenna
        }
      }
      setNeighbors(arrayToSave);
      //check that there are at least 3 neightbors
      return minimumOfThreeNeighborsRule(inRange, updateRateLessThanTwo);
    }
    return true;
  }

  function distanceFormula(x1: number, y1: number, x2: number, y2: number) {
    return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
  }

  function antennaRules(inRange: number) {
    // if there are less than five devices (excluding gateway and antenna, all the devices must be in range of the antenna)
    if (activeNetwork!.deviceInstances.length <= 5 && inRange !== activeNetwork!.deviceInstances.length - 1) {
      //rule conditions not met
      notify(getString("nwk_rules_antenna_devices"));
      return false;
    } else if (activeNetwork!.deviceInstances.length > 5 && inRange < 5) {
      //rule conditions not met
      notify(getString("nwk_rules_antenna_devices_min"));
      return false;
    }
    //rule of percentages -- 25% of devices in network are in range of gateway (antenna)
    if (inRange / (activeNetwork!.deviceInstances.length - 1) < 0.25) {
      //rule conditions not met
      notify(getString("nwk_rules_percentages "));
      return false;
    }
    //Antenna rules passed
    return true;
  }

  function minimumOfThreeNeighborsRule(inRange: number, updateRateLessThanTwo: string = "") {
    if (inRange < 3) {
      notify(getString("nwk_rules_device_min_neighbors") + updateRateLessThanTwo);
      return false;
    }
    return true;
  }

  function obstructionsCheck(x1: number, y1: number, x2: number, y2: number) {
    var lOS = true;
    obstructionInstances.some((obs) => {
      lOS = checkLineOfSight(
        x1,
        y1,
        x2,
        y2,
        obs.xPosition,
        obs.yPosition,
        obs.width! * scalingFactor!,
        obs.length! * scalingFactor!,
        obs.obstructionTypeID === ObstructionType.Rectangle,
      );
      if (!lOS) {
        return true;
      }
      return false;
    });
    return lOS;
  }

  function updatedObstructionCheck() {
    if (deviceType === DeviceCategory.Gateway || deviceType === -1) {
      return 0;
    }
    var newNeighbors: number[] = [];
    allNeighbors.forEach((neighbor) => {
      //if neighbor NOT obstructed
      var check = obstructionsCheck(
        centerX! / plotImageTransform,
        centerY! / plotImageTransform,
        activeNetwork!.deviceInstances[neighbor].xPosition,
        activeNetwork!.deviceInstances[neighbor].yPosition,
      );
      if (check) {
        newNeighbors = [...newNeighbors, neighbor];
      }
    });
    setDevicesInRange(newNeighbors);
    if (deviceType === DeviceCategory.Antenna) {
      setCompliant(antennaRules(newNeighbors.length));
    } else if (deviceType !== DeviceCategory.Gateway && deviceType !== -1) {
      setCompliant(minimumOfThreeNeighborsRule(newNeighbors.length));
      // Check if Antenna is a neighbor (if deviceType is not an antenna)
      var check = obstructionsCheck(
        centerX! / plotImageTransform,
        centerY! / plotImageTransform,
        activeNetwork!.deviceInstances[0].xPosition,
        activeNetwork!.deviceInstances[0].yPosition,
      );
      if (check) {
        newNeighbors = [...newNeighbors, 0];
      }
    }
    setNeighbors(newNeighbors);
  }

  // returns false if the line of sight is blocked, returns true if line of sight is unblocked
  function checkLineOfSight(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    h: number,
    k: number,
    w: number,
    l: number,
    isRect: boolean,
  ) {
    // width and length are assumed to be passed in as pixels, not feet/meters
    const leftMostX = x1 <= x2 ? x1 : x2;
    const rightMostX = leftMostX === x1 ? x2 : x1;
    const topMostY = y1 <= y2 ? y1 : y2;
    const bottomMostY = topMostY === y1 ? y2 : y1;
    //find equation of the line (y = mx + b)
    const m = (y2 - y1) / (x2 - x1); //slope of line
    const yIntercept = y1 - m * x1; // y intercept
    // (h, k) center of obstruction
    if (isRect) {
      //if obstruction rectangle
      const left = h - w / 2; // top-left x coord
      const top = k - l / 2; // top-left y coord
      const right = left + w; // bottom-right x coord
      const bottom = top + l; // bottom-right y coord
      //LEFT line (x)
      var intersect = m * left + yIntercept;
      //check if intersect is on the segment of line in the rectangle
      if (intersect >= top && intersect <= bottom && left > leftMostX && left < rightMostX) {
        return false;
      }
      //  TOP line (y)
      intersect = (top - yIntercept) / m;
      if (intersect >= left && intersect <= right && top > topMostY && top < bottomMostY) {
        return false;
      }
      //  RIGHT line (x)
      intersect = m * right + yIntercept;
      if (intersect >= top && intersect <= bottom && right > leftMostX && right < rightMostX) {
        return false;
      }
      //  BOTTOM line (y)
      intersect = (bottom - yIntercept) / m;
      if (intersect >= left && intersect <= right && bottom > topMostY && bottom < bottomMostY) {
        return false;
      }
      // if no points of intersection are found on any of the four lines, the line of sight is not blocked
      return true;
    } else {
      // else obstruction is ellipse
      const leftX = h - w / 2;
      const leftY = k;
      const topX = h;
      const topY = k - l / 2;
      const rightX = h + w / 2;
      const rightY = k;
      const bottomX = h;
      const bottomY = k + l / 2;
      const calculateA = (h: number, k: number, x: number, y: number) => (y - k) / (x ** 2 - 2 * h * x + h ** 2);
      const calculateB = (a: number, h: number, m: number) => -2 * a * h - m;
      const calculateC = (a: number, h: number, k: number, b: number) => h ** 2 * a + k - b;

      //topLeft quadrant: vertex: (topX, topY), point: (leftX, leftY)
      var A = calculateA(topX, topY, leftX, leftY);
      var B = calculateB(A, topX, m);
      var C = calculateC(A, topX, topY, yIntercept);
      var iX = quadraticFormula(A, B, C);

      if (iX) {
        var iy1 = iX.x1 * m + yIntercept;
        var iy2 = iX.x2 * m + yIntercept;
        if (
          iX.x1 <= topX &&
          iX.x1 >= leftX &&
          iy1 <= leftY &&
          iy1 >= topY &&
          iX.x1 > leftMostX &&
          iX.x1 < rightMostX &&
          iy1 > topMostY &&
          iy1 < bottomMostY
        ) {
          return false;
        }
        if (
          iX.x2 <= topX &&
          iX.x2 >= leftX &&
          iy2 <= leftY &&
          iy2 >= topY &&
          iX.x2 > leftMostX &&
          iX.x2 < rightMostX &&
          iy2 > topMostY &&
          iy2 < bottomMostY
        ) {
          return false;
        }
      }
      //topRight quadrant: vertex: (topX, topY), point: (rightX, rightY)
      A = calculateA(topX, topY, rightX, rightY);
      B = calculateB(A, topX, m);
      C = calculateC(A, topX, topY, yIntercept);
      iX = quadraticFormula(A, B, C);

      if (iX) {
        iy1 = iX.x1 * m + yIntercept;
        iy2 = iX.x2 * m + yIntercept;
        if (
          iX.x1 <= rightX &&
          iX.x1 >= topX &&
          iy1 <= rightY &&
          iy1 >= topY &&
          iX.x1 > leftMostX &&
          iX.x1 < rightMostX &&
          iy1 > topMostY &&
          iy1 < bottomMostY
        ) {
          return false;
        }
        if (
          iX.x2 <= rightX &&
          iX.x2 >= topX &&
          iy2 <= rightY &&
          iy2 >= topY &&
          iX.x2 > leftMostX &&
          iX.x2 < rightMostX &&
          iy2 > topMostY &&
          iy2 < bottomMostY
        ) {
          return false;
        }
      }
      //bottomLeft quadrant: vertex: (bottomX, bottomY), point: (leftX, leftY)
      A = calculateA(bottomX, bottomY, leftX, leftY);
      B = calculateB(A, bottomX, m);
      C = calculateC(A, bottomX, bottomY, yIntercept);
      iX = quadraticFormula(A, B, C);
      if (iX) {
        iy1 = iX.x1 * m + yIntercept;
        iy2 = iX.x2 * m + yIntercept;
        if (
          iX.x1 <= bottomX &&
          iX.x1 >= leftX &&
          iy1 <= bottomY &&
          iy1 >= leftY &&
          iX.x1 > leftMostX &&
          iX.x1 < rightMostX &&
          iy1 > topMostY &&
          iy1 < bottomMostY
        ) {
          return false;
        }
        if (
          iX.x2 <= bottomX &&
          iX.x2 >= leftX &&
          iy2 <= bottomY &&
          iy2 >= leftY &&
          iX.x2 > leftMostX &&
          iX.x2 < rightMostX &&
          iy2 > topMostY &&
          iy2 < bottomMostY
        ) {
          return false;
        }
      }
      //bottomRight quadrant: vertex: (bottomX, bottomY), point: (rightX, rightY)
      A = calculateA(bottomX, bottomY, rightX, rightY);
      B = calculateB(A, bottomX, m);
      C = calculateC(A, bottomX, bottomY, yIntercept);
      iX = quadraticFormula(A, B, C);
      if (iX) {
        iy1 = iX.x1 * m + yIntercept;
        iy2 = iX.x2 * m + yIntercept;
        if (
          iX.x1 <= rightX &&
          iX.x1 >= bottomX &&
          iy1 <= bottomY &&
          iy1 >= rightY &&
          iX.x1 > leftMostX &&
          iX.x1 < rightMostX &&
          iy1 > topMostY &&
          iy1 < bottomMostY
        ) {
          return false;
        }
        if (
          iX.x2 <= rightX &&
          iX.x2 >= bottomX &&
          iy2 <= bottomY &&
          iy2 >= rightY &&
          iX.x2 > leftMostX &&
          iX.x2 < rightMostX &&
          iy2 > topMostY &&
          iy2 < bottomMostY
        ) {
          return false;
        }
      }
      return true;
    }
  }

  function quadraticFormula(a: number, b: number, c: number) {
    var discriminant = b ** 2 - 4 * a * c;
    if (discriminant < 0) {
      return null;
    }
    var x1 = (-1 * b + Math.sqrt(discriminant)) / (2 * a);
    var x2 = (-1 * b - Math.sqrt(discriminant)) / (2 * a);
    return { x1: x1, x2: x2 };
  }

  function notify(message: string) {
    enqueueNotification(message.replace(/\s/g, ""), message, "warning", currentBatchID);
  }

  return (
    <>
      <div
        onDragStart={(e) => handleOnDrag(e)}
        onDrag={(e) => handleOnDragging(e)}
        onDragEnd={(e) => handleOnDrop(e)}
        style={{
          position: "absolute",
          left: calculatedX,
          top: calculatedY,
          zIndex: "4",
          transform: `scale(${iconPercent / 100 / (zoomPercent / 100)})`,
        }}
        onClick={(e) => selectDevice(e)}
        onContextMenu={(e) => openMenu(e)}
        onDoubleClick={handleDoubleClick}
      >
        <img
          draggable
          src={deviceIcon ?? imageUrl}
          alt="device icon"
          style={{
            height: iconHW.toString() + "px",
            width: iconHW.toString() + "px",
            outline:
              deviceType === DeviceCategory.Antenna && antennaOutOfRange
                ? `5px solid red`
                : selected
                  ? `3px solid ${theme.palette.secondary.light}`
                  : "none",
            cursor: "grab",
          }}
        ></img>
        {selected && name !== "" && (
          <Typography
            variant="caption"
            display="block"
            sx={{
              userSelect: "none",
              bgcolor: "white",
              pl: "2px",
              pr: "2px",
              position: "absolute",
              top: "43px",
              left: "0px",
              whiteSpace: "nowrap",
            }}
            draggable="false"
          >
            {name}
          </Typography>
        )}
      </div>
      <Menu
        open={openDeviceMenu}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
          setOpenDeviceMenu(false);
        }}
      >
        <MenuItem
          onClick={() => {
            setOpenDeviceMenu(false);
            handlePropertiesRequest();
          }}
        >
          {getString("cmn_properties")}
        </MenuItem>
        <MenuItem
          disabled={scalingFactor === null || scalingFactor <= 0}
          onClick={() => {
            setOpenDeviceMenu(false);
            setShowDeviceRange(!showDeviceRange);
          }}
        >
          {showDeviceRange ? getString("mnu_hide_range") : getString("mnu_show_range")}
        </MenuItem>
        {deviceType !== DeviceCategory.Gateway && deviceType !== DeviceCategory.Antenna && (
          <MenuItem
            onClick={() => {
              setOpenDeviceMenu(false);
              handleCopy();
            }}
          >
            {getString("cmn_copy")}
          </MenuItem>
        )}
        {deviceType !== DeviceCategory.Gateway && deviceType !== DeviceCategory.Antenna && (
          <MenuItem
            onClick={() => {
              setOpenDeviceMenu(false);
              deleteDevice(instanceIndex);
            }}
          >
            {getString("cmn_delete")}
          </MenuItem>
        )}
      </Menu>
      {(!compliant || (calculationsToggle && !path) || showDeviceRange) && (
        <DeviceRange
          range={range}
          x={calculatedX ? calculatedX + iconHW / 2 : 0}
          y={calculatedY ? calculatedY + iconHW / 2 : 0}
          valid={compliant && path}
        />
      )}
      {multiDrag.length > 1 && cursorOnDragX && cursorOnDragY && (
        <DragSelected
          x={calculatedX! + (cursorOnDragX - cursorX) / (zoomPercent / 100) + iconHW / 2}
          y={calculatedY! + (cursorOnDragY - cursorY) / (zoomPercent / 100) + iconHW / 2}
          iconSize={(iconHW * (iconPercent / 100)) / (zoomPercent / 100)}
          devices={multiDragShadow}
        />
      )}
    </>
  );
};

export default DroppedDevice;
